import {
  useState,
  useRef,
  useEffect,
  Children,
  isValidElement,
  type FC,
  type ReactNode,
} from "react";
import type { RenderConversation } from "./types";
import {
  ConversationRenderer,
  VIEW_CONFIG,
  type ViewPreset,
} from "./ConversationRenderer";

export type Theme = "light" | "dark" | "auto";

// ============================================================================
// ConversationTab - Child component for each tab
// ============================================================================

export type ConversationTabProps = {
  /** Optional label for the tab (falls back to conversation.meta.title) */
  label?: string;
  /** Conversation data to render */
  conversation: RenderConversation;
};

/**
 * Represents a single tab in a TabbedConversation.
 * Must be used as a direct child of TabbedConversation.
 */
export const ConversationTab: FC<ConversationTabProps> = () => {
  // This component doesn't render directly - TabbedConversation extracts its props
  return null;
};

// ============================================================================
// TabbedConversation - Wrapper component with tab bar
// ============================================================================

export type TabbedConversationProps = {
  /** ConversationTab children */
  children: ReactNode;
  /**
   * Theme mode:
   * - "light" = force light theme
   * - "dark" = force dark theme
   * - "auto" = inherit from parent (no class applied)
   * @default "auto"
   */
  theme?: Theme;
  /** View preset (default: "responsive" for embedding in content) */
  view?: ViewPreset;
  /** Fixed height for scroll container in pixels (default: 500) */
  height?: number;
  /** Optional className for the outer container */
  className?: string;
};

type ExtractedTab = {
  label: string;
  conversation: RenderConversation;
};

/**
 * A tabbed conversation viewer that displays multiple conversations
 * in a compact interface with a tab bar.
 *
 * @example
 * <TabbedConversation height={520}>
 *   <ConversationTab label="Basic Example" conversation={convo1} />
 *   <ConversationTab label="Advanced" conversation={convo2} />
 * </TabbedConversation>
 */
export const TabbedConversation: FC<TabbedConversationProps> = ({
  children,
  theme = "auto",
  view = "responsive",
  height = 500,
  className = "",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when switching tabs
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeIndex]);

  // Extract tab data from children
  const tabs: ExtractedTab[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === ConversationTab) {
      const props = child.props as ConversationTabProps;
      tabs.push({
        label:
          props.label ||
          props.conversation?.meta?.title ||
          `Tab ${tabs.length + 1}`,
        conversation: props.conversation,
      });
    }
  });

  // Guard against empty tabs
  if (tabs.length === 0) {
    return null;
  }

  const activeTab = tabs[activeIndex];
  const viewConfig = VIEW_CONFIG[view];

  // Determine theme class
  const themeClass = theme === "auto" ? "" : theme === "dark" ? "dark" : "";

  // Combine classes
  const outerClassName = [
    "conversation-renderer",
    "tabbed-conversation",
    themeClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={outerClassName}>
      <div
        className="tabbed-conversation__frame"
        style={{
          width: viewConfig.width,
          maxWidth: viewConfig.maxWidth,
          margin: "0 auto",
        }}
      >
        {/* Tab bar */}
        <div className="tabbed-conversation__tabs" role="tablist">
          {tabs.map((tab, index) => (
            <button
              key={index}
              role="tab"
              aria-selected={index === activeIndex}
              className={`tabbed-conversation__tab ${index === activeIndex ? "tabbed-conversation__tab--active" : ""}`}
              onClick={() => setActiveIndex(index)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content area */}
        <div
          ref={contentRef}
          className="tabbed-conversation__content"
          style={{ height }}
          role="tabpanel"
        >
          <ConversationRenderer
            conversation={activeTab.conversation}
            view={view}
            signalReady={false}
          />
        </div>
      </div>
    </div>
  );
};
